import { useEffect, useState } from "react";
import { IHall, IObject, ITable, WorkTimeForADay } from "../App";
import { TableReservation as TableReservationComponent } from "./TableReservation";
import { ISingleReservation, IWholeHallReservation, locale, t } from "../MainApp";
import { StandingReservation } from "./StandingReservation";
import { ReservationDatePicker } from "../sharedcomponents/ReservationDatePicker";
import { StartTimePicker } from "../sharedcomponents/StartTimePicker";
import { EndTimePicker } from "../sharedcomponents/EndTimePicker";
import { CommentComponent } from "../sharedcomponents/CommentComponent";
import { Button, Divider, Modal, Typography } from "antd";
import { apiFetchWholeHallReservationTotalDue, apiMakeWholeHallReservation, apiUpdateWholeHallReservation, makeTimestampFromDateAndDate } from "../api/API";
import dayjs, { Dayjs } from "dayjs";
import HallOccupancyGraph from "../sharedcomponents/HallOccupancyGraph";
import useNotification from "antd/es/notification/useNotification";
import { IsHallEmptyForSelectedTimeIntervalChecker } from "./IsHallEmptyForSelectedTimeIntervalChecker";
import { TimeChecker } from "../sharedcomponents/TimeChecker";
import { PaymentTableWholeHallReservation, TPaymentData } from "../paymentcomponents/PaymentTableWholeHallReservation";

type IMinMaxGuest = {
    minGuests: number;
    maxGuests: number;
}

type GuestsList = {
    guests: string[];
};

export type TableReservation = IMinMaxGuest & GuestsList & {
    table: ITable;
};

export type HallReservation = IMinMaxGuest & GuestsList;

type WholeHallProperties = {
    cybarWorktime?: WorkTimeForADay[];
    object: IObject;
    selectedHall: IHall;

    locale: locale;
    onMakeReservation: (rid: number) => void;
    existingWholeHallReservation?: IWholeHallReservation;
};

export function WholeHallReservation(props: WholeHallProperties) {
    let [tableReservations, setTableReservations] = useState<TableReservation[]>([]);
    let [hallReservations, setHallReservations] = useState<HallReservation[]>([]);

    let [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
    let [startTime, setStartTime] = useState<Dayjs | null>(null);
    let [endTime, setEndTime] = useState<Dayjs | null>(null);
    let [comment, setComment] = useState<string>(``);

    let [notificationMessage, setNotificationMessage] = useState<string>('');
    let [api, contextHolder] = useNotification({ placement: "topRight", maxCount: 1 });

    let [paymentDueModalOpened, setPaymentDueModalOpened] = useState<boolean>(false);
    let [paymentData, setPaymentData] = useState<TPaymentData | undefined>(undefined);

    let [disabledDuration, setDisabledDuration] = useState<number>(0);

    useEffect( () => {
        setSelectedDate(null);
        setStartTime(null);
        setEndTime(null);
    }, [props.selectedHall]);

    useEffect( () => {
        if(startTime) {
            if(props.existingWholeHallReservation && props.existingWholeHallReservation.can_change_reservation_duration) {
                setDisabledDuration(0);
                if(startTime && selectedDate && props.selectedHall.minimumDuration) {
                    const date = new Date(selectedDate?.year(), selectedDate?.month(), selectedDate?.date(), startTime.hour(), startTime.minute(), 0, 0);
                    const newEndDate = dayjs(date.getTime() + props.selectedHall.minimumDuration * 60 * 1000 + 1000);

                    let worktime = props.object.worktime[date.getDay()];
                    if(worktime) {
                        const maxEndDate = new Date(selectedDate.year(), selectedDate.month(), selectedDate.date(), worktime.endHour, worktime.endMinute, 0, 0);
                        if( dayjs(newEndDate).isAfter(maxEndDate)) {
                            setEndTime(dayjs(maxEndDate));
                        }
                    } else {
                        setEndTime(newEndDate);    
                    }                                        
                }
            } else if(props.existingWholeHallReservation && !props.existingWholeHallReservation.can_change_reservation_duration) {
                const sr = props.existingWholeHallReservation.reservations[0];                
                const duration = (sr.endDate - sr.startDate);
                setDisabledDuration(duration + 1000);
                setEndTime(startTime.add(duration, 'milliseconds'));    
            }
        }
    }, [props.existingWholeHallReservation, props.selectedHall.minimumDuration, selectedDate, startTime]);

    useEffect(() => {
        setTableReservations(() => {
            let tableReservations: TableReservation[] = [];
            let hallReservations: HallReservation[] = [];

            // reserve 1 place in all the tables in the hall
            let reservedPlaces = 0;

            for (const table of props.selectedHall.tables) {

                let tableReservation: TableReservation = {
                    table: table,
                    minGuests: 1,
                    maxGuests: 1,
                    guests: [``]
                };

                if (reservedPlaces < props.selectedHall.capacity) {
                    tableReservations.push(tableReservation);
                    ++reservedPlaces;
                }
            }

            // calculate remaining available seats
            let remainingPlaces = props.selectedHall.capacity - reservedPlaces;

            let hasFreeTableSeats = () => {
                for (const tableReservation of tableReservations) {
                    if (tableReservation.table.seats > tableReservation.minGuests) {
                        return true;
                    }
                }

                return false;
            };

            while (remainingPlaces > 0 && hasFreeTableSeats()) {
                for (let idx = 0; idx < tableReservations.length; ++idx) {
                    let tableReservation = tableReservations[idx];

                    if (tableReservation.table.seats > tableReservation.minGuests) {
                        tableReservation.minGuests++;
                        tableReservation.maxGuests++;
                        tableReservation.guests.push(``);

                        --remainingPlaces;

                        if (remainingPlaces === 0) {
                            break;
                        }
                    }
                }
            }

            setHallReservations(() => {
                if (props.selectedHall.rezervationSizeLimit > 0) {
                    while (remainingPlaces > 0) {
                        let filledPlace = false;
                        for (const hallReservation of hallReservations) {
                            if (props.selectedHall.rezervationSizeLimit > hallReservation.minGuests) {
                                hallReservation.minGuests++;
                                hallReservation.maxGuests++;
                                hallReservation.guests.push(``);

                                --remainingPlaces;
                                filledPlace = true;
                            }
                        }
                        if (!filledPlace) {
                            hallReservations.push({ minGuests: 0, maxGuests: 0, guests: [] });
                        }
                    }
                }

                return hallReservations;
            });

            return tableReservations;
        });
    }, [props.selectedHall.capacity, props.selectedHall.rezervationSizeLimit, props.selectedHall.tables]);

    useEffect( () => {        
        if(notificationMessage.length > 0) {
            api.error({
                message: notificationMessage,
                description: notificationMessage,
            });
        }
        setNotificationMessage(``);
    }, [notificationMessage]);

    useEffect( () => {
        if(props.existingWholeHallReservation) {
            setComment(props.existingWholeHallReservation.comment ? props.existingWholeHallReservation.comment : '');
            
            if(props.existingWholeHallReservation.reservations.length > 0) {
                setSelectedDate(dayjs(props.existingWholeHallReservation.reservations[0].startDate));
                setStartTime(dayjs(props.existingWholeHallReservation.reservations[0].startDate));
                setEndTime(dayjs(props.existingWholeHallReservation.reservations[0].endDate));                
            }

            let tr: ISingleReservation[] = [];
            let hr: ISingleReservation[] = [];
            for (const reservation of props.existingWholeHallReservation.reservations) { 
                if(reservation.tableId !== null) {
                    tr.push(reservation);
                } else {
                    hr.push(reservation);
                }
            }

            setTableReservations(prevTables => {
                let prevTablesCopy = [...prevTables];

                setHallReservations( prevHalls => {
                    let copy = [...prevHalls];
                    for(let i = 0; i < Math.min(hr.length, copy.length); i++) {                        
                        copy[i].guests = [...hr[i].guests];
                    }    
                    return copy;
                });

                for (const reservation of tr) {
                    let tableIdx = prevTables.findIndex( tr => tr.table.id === reservation.tableId );
                    if(tableIdx !== -1) {                        
                        let newTableReservation = prevTables[tableIdx];
                        newTableReservation.guests = [...reservation.guests];
                        prevTablesCopy.splice(tableIdx, 1, newTableReservation);                                            
                    }
                }

                return prevTablesCopy;
            });
        }
    }, [props.selectedHall, props.existingWholeHallReservation]);



    function renderAllTables() {
        let container = [];
        for (let idx = 0; idx < tableReservations.length; idx++) {
            let tableReservation = tableReservations[idx];
            container.push(
                <>
                    <TableReservationComponent
                        locale={props.locale}
                        table={tableReservation.table}
                        minGuests={tableReservation.minGuests}
                        maxGuests={tableReservation.maxGuests}
                        onChangeGuestName={(seatNo, name) => {
                            setTableReservations((prevTableReservations) => {
                                    let newTableReservations = [...prevTableReservations];
                                    let theTableReservation = newTableReservations.find((value, index) => {
                                        return index === idx;
                                    });

                                    if (theTableReservation) {
                                        theTableReservation.guests[seatNo] = name;
                                    }

                                    return newTableReservations;
                                }
                            )}
                        }
                        tableGuests={tableReservation.guests}
                        onAddTableGuest={() => {
                            setTableReservations((prevTableReservations) => {
                                    let copy = [...prevTableReservations];
                                    let theTableReservation = copy.find((value, index) => {
                                        return index === idx;
                                    });
                                    if (theTableReservation) {
                                        theTableReservation.guests.push(``);
                                    }

                                    return copy;
                            })
                        }}

                        onRemoveTableGuest={(seatNo) => {
                            setTableReservations((prevTableReservations) => {
                                    let copy = [...prevTableReservations];
                                    let theTableReservation = copy.find((value, index) => {
                                        return index === idx;
                                    });
                                    if (theTableReservation) {
                                        theTableReservation.guests.splice(seatNo, 1);
                                    }
                                    return copy;
                            });
                        }}
                    />
                    <br /><br />
                </>
            );
        }

        return container;
    }

    function renderAllStandingReservations() {
        let container = [];
        for (let idx = 0; idx < hallReservations.length; ++idx) {
            let hallReservation = hallReservations[idx];
            container.push(
                <StandingReservation
                    locale={props.locale}
                    hall={props.selectedHall}
                    minGuests={hallReservation.minGuests}
                    maxGuests={hallReservation.maxGuests}
                    standingGuests={hallReservation.guests}
                    onAddGuest={() => {
                        setHallReservations(prevHallReservations => {
                                const copyOfHalls = [...prevHallReservations];
                                let standingHallReservation = copyOfHalls.find((hall, index) => {
                                    return index === idx;
                                });

                                if (standingHallReservation) {
                                    standingHallReservation.guests.push(``);
                                }

                                return copyOfHalls;
                        });
                    }}
                    onRemoveGuest={(seatNo) => {
                        setHallReservations(prevHallReservations => {
                                const copyOfHalls = [...prevHallReservations];
                                let standingHallReservation = copyOfHalls.find((hall, index) => {
                                    return index === idx;
                                });

                                if (standingHallReservation) {
                                    standingHallReservation.guests.splice(seatNo, 1);
                                }

                                return copyOfHalls;
                        });
                    }}

                    onChangeGuestName={(seatNo, name) => {
                        setHallReservations(prevHallReservations => {
                                const copyOfHalls = [...prevHallReservations];
                                let standingHallReservation = copyOfHalls.find((hall, index) => {
                                    return index === idx;
                                });

                                if (standingHallReservation) {
                                    standingHallReservation.guests[seatNo] = name;
                                }

                                return copyOfHalls;
                        });
                    }}
                />
            );
        }
    
        return container;
    }

    function sendMakeWholeHallReservation() {
        if(selectedDate && startTime && endTime) {
            apiMakeWholeHallReservation(
                props.object.id,
                props.selectedHall.id,
                selectedDate,
                startTime,
                endTime,
                tableReservations,
                hallReservations,
                comment
            ).then( response => response.json() )
            .then( json => {
                if(json.success === true) {
                    props.onMakeReservation(0);
                    setPaymentDueModalOpened(false);                                        
                } else {
                    setNotificationMessage(json.message[props.locale]);
                }                        
            });
        }
    }

    function isTimeNowInsideCybarsWorktime() {
        if(props.cybarWorktime) {
            const now = dayjs(Date.now());
            const worktime = props.cybarWorktime[now.day()];
            const cybarStartToday = dayjs(new Date(now.year(), now.month(), now.date(), worktime.startHour, worktime.startMinute, 0, 0));
            const cybarEndToday = dayjs(new Date(now.year(), now.month(), now.date(), worktime.endHour, worktime.endMinute, 0, 0));
            if(now.unix() >= cybarStartToday.unix() && now.unix() <= cybarEndToday.unix()) {
                return true;
            } else {
                return false;
            }
        }

        return undefined;
    }


    return (
        <> 
            {contextHolder} 


            <Divider><Typography.Title level={5}>{t(props.locale, "choosedate")}</Typography.Title></Divider>
            <ReservationDatePicker
                disabled={false}
                locale={props.locale}
                object={props.object}
                selectedDate={selectedDate}
                onSelectedDateChange={(date) => {
                    setSelectedDate(date);
                    setStartTime(null);
                    setEndTime(null);
                }}
            />

            {selectedDate ?
                <HallOccupancyGraph
                    date={selectedDate.toDate()}
                    hall={props.selectedHall}
                    object={props.object}
                />
                : <></>
            }

            <Divider><Typography.Title level={5}>{t(props.locale, "from")}</Typography.Title></Divider>
            <StartTimePicker
                disabled={false}
                format="HH:mm"
                object={props.object}
                selectedDate={selectedDate?.toDate()}
                reservationStartTime={startTime}
                onChangeReservationStartTime={(startTime) => {
                    setStartTime(startTime ? dayjs(new Date(startTime.year(), startTime.month(), startTime.date(), startTime.hour(), startTime.minute(), 1, 0)): null);
                }}
                disabledMinutesFromEnd={Math.floor(disabledDuration / 60 / 1000)}                
            />

            <Divider><Typography.Title level={5}>{t(props.locale, "to")}</Typography.Title></Divider>
            <EndTimePicker
                format="HH:mm"
                disabled={props.existingWholeHallReservation !== undefined && props.existingWholeHallReservation.can_change_reservation_duration === false}
                endTime={endTime}
                onChangeEndTime={(endTime) => setEndTime(endTime)}
                object={props.object}
                offsetStart={props.selectedHall.minimumDuration && startTime ? props.selectedHall.minimumDuration : undefined}
                selectedDate={selectedDate?.toDate()}
                startTime={startTime !== null ? startTime : undefined}
            />

            {startTime && endTime
            ? <TimeChecker
            start={startTime}
            end={endTime}
            locale={props.locale}
            />
            :<></>}

            {selectedDate && startTime && endTime 
            ? <IsHallEmptyForSelectedTimeIntervalChecker
                object={props.object}
                hall={props.selectedHall}
                date={selectedDate}
                start={startTime}
                end={endTime}
                />
            : <></> 
            }

            <Divider><Typography.Title level={5}>{t(props.locale, "guests")}</Typography.Title></Divider>
            {renderAllTables()}
            {renderAllStandingReservations()}

            <Divider><Typography.Title level={5}>{t(props.locale, "reservationcomment")}</Typography.Title></Divider>
            <CommentComponent
                comment={comment}
                locale={props.locale}
                onChangeComment={(comment) => setComment(comment)}
            />

            <Button style={{ width: '100%' }} type="primary" onClick={(event) => {
                event.preventDefault();


                if (selectedDate && startTime && endTime) {
                    
                    if(!props.existingWholeHallReservation ) {                        

                        apiFetchWholeHallReservationTotalDue(
                            props.object.id,
                            props.selectedHall.id,
                            makeTimestampFromDateAndDate(selectedDate.toDate(), startTime.toDate()),
                            makeTimestampFromDateAndDate(selectedDate.toDate(), endTime.toDate()),
                            ).then( response => response.json() )
                            .then( json => {
                                if(json.type === "free" && json.schema_version === 2) {
                                    setPaymentData(json);                         
                                    sendMakeWholeHallReservation();       
                                } else if (json.type === "payable" && json.schema_version === 2) {
                                    let cybarWorking = isTimeNowInsideCybarsWorktime();                        
                                    if(!cybarWorking) {
                                        setNotificationMessage("New Reservations that must be paid may only be made when Cybar is working!!!");
                                        return ;
                                    }
                                    setPaymentData(json);
                                    setPaymentDueModalOpened(true);
                                } else {
                                    const errorMessage = `Unimplemented type ${json.type} and/or version ${json.schema_version} response!!!`;
                                    console.error(errorMessage);
                                    throw Error(errorMessage);
                                }                
                            }).catch( reason => {
                                console.log(`${reason}`);
                            });
                    } else {
                        apiUpdateWholeHallReservation(
                            props.existingWholeHallReservation.id,
                            selectedDate,
                            dayjs(makeTimestampFromDateAndDate(selectedDate.toDate(), startTime.toDate())),
                            dayjs(makeTimestampFromDateAndDate(selectedDate.toDate(), endTime.toDate())),                            
                            tableReservations,
                            hallReservations,
                            comment
                        ).then( response => { 
                            if(response.status === 200) {
                                setNotificationMessage("Reservation Successfully Updated");
                                props.onMakeReservation(0);
                            }
                            return response.json(); }
                        )
                        .then( json => {
                            setNotificationMessage(json.message[props.locale]);
                        }).catch( reason => {
                            setNotificationMessage(reason)
                            console.error(reason);
                        })
                    }
                }
            }}>
                {t(props.locale, "confirm")}
            </Button>

            {!props.existingWholeHallReservation && selectedDate && startTime && endTime ? 
            <Modal 
                    open={paymentDueModalOpened}
                    title={t(props.locale, 'payment_due')}                        
                    width={'fit-content'}
                    onOk={(e) => {   
                        sendMakeWholeHallReservation();                     
                    }}                            
                    onCancel={(e) => {
                        setPaymentDueModalOpened(false);
                    }}                    
                >                    
                    <PaymentTableWholeHallReservation 
                        locale={props.locale}
                        payableData={paymentData}
                    />         
                </Modal> : <></> }
        </>
    );
}